/**
 * Super Admin cross-brand Audit log.
 *
 * Filters:
 *   - brand multi-select (chips)
 *   - action search (substring match server-side via exact action param,
 *     plus client-side `q` filter for friendlier UX)
 *   - from / to date range
 *
 * Backend supports a single `brand_id` filter at a time, so when the
 * user picks multiple brands we fan-out queries and merge — keeping
 * keys distinct per brand (Mandate 2: cache isolation).
 */
import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Search, ScrollText, X } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { formatDateTime } from '@/lib/utils';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, JsonViewer,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import { Input } from '@/components/ui_primitives/Field';
import Button from '@/components/ui_primitives/Button';

const LIMIT = 100;

export default function AdminAuditPage() {
  const token = useAuthStore((s) => s.token);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [expanded, setExpanded] = useState(null);

  const brandsQ = useQuery({
    queryKey: qk.adminBrands(),
    queryFn: () => api.adminBrands(token),
    enabled: !!token,
  });

  const filters = useMemo(() => {
    const f = { limit: LIMIT };
    if (from) f.from = from;
    if (to) f.to = to;
    return f;
  }, [from, to]);

  // No-brand-filter pool query
  const allQ = useQuery({
    queryKey: qk.adminAudit({ ...filters, brands: selectedBrands.join(',') || 'all' }),
    queryFn: () => api.adminAuditLogs(token, filters),
    enabled: !!token && selectedBrands.length === 0,
  });

  // Fan-out per selected brand (keys carry brand_id — Mandate 2)
  const perBrandQs = useQueries({
    queries: selectedBrands.map((bid) => ({
      queryKey: ['admin-audit-brand', bid, filters],
      queryFn: () => api.adminAuditLogs(token, { ...filters, brand_id: bid }),
      enabled: !!token,
    })),
  });

  const isLoading = (selectedBrands.length === 0 ? allQ.isLoading : perBrandQs.some((x) => x.isLoading));
  const error = (selectedBrands.length === 0 ? allQ.error : perBrandQs.find((x) => x.error)?.error);

  if (isLoading) return <FullSpinner />;
  if (error) return <QueryError error={error} onRetry={() => window.location.reload()} />;

  // Backend returns a bare list at /api/admin/audit-logs (server.py route wins
  // over admin.py's {items,next_cursor} version). Be tolerant of both shapes.
  const unwrap = (d) => (Array.isArray(d) ? d : (d?.items || []));

  let items = [];
  if (selectedBrands.length === 0) {
    items = unwrap(allQ.data);
  } else {
    // Even though we send brand_id to the backend, defensively filter
    // client-side by metadata.brand_id so a non-filtering backend can't
    // leak other brands' events into a brand-scoped view.
    const allowed = new Set(selectedBrands);
    items = perBrandQs
      .flatMap((q2) => unwrap(q2.data))
      .filter((row) => allowed.has(row?.metadata?.brand_id));
    items.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    // De-dupe by id in case the same event was returned by multiple
    // per-brand queries (shouldn't happen, but be defensive).
    const seen = new Set();
    items = items.filter((r) => {
      if (seen.has(r.id)) return false;
      seen.add(r.id);
      return true;
    });
  }
  if (q.trim()) {
    const needle = q.toLowerCase();
    items = items.filter((row) =>
      [row.action, row.target_type, row.actor_email, JSON.stringify(row.metadata)]
        .filter(Boolean).some((s) => String(s).toLowerCase().includes(needle))
    );
  }

  const brandsById = new Map((brandsQ.data || []).map((b) => [b.id, b]));

  return (
    <div className="space-y-6" data-testid="admin-audit-page">
      <PageHeader
        kicker="Audit"
        title="Cross-brand audit log"
        subtitle="Filter by brand, action, actor or date range. Click a row to expand its metadata."
      />

      <SectionCard padded>
        <div className="grid lg:grid-cols-4 gap-3 mb-5">
          <div className="lg:col-span-2 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search action / target / actor / metadata…" testID="audit-search" />
          </div>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} testID="audit-from" />
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} testID="audit-to" />
        </div>

        <div className="mb-5">
          <div className="text-[11px] uppercase tracking-widest text-gold/80 font-semibold mb-2">Brand filter</div>
          <div className="flex flex-wrap gap-2">
            {(brandsQ.data || []).map((b) => {
              const active = selectedBrands.includes(b.id);
              return (
                <button key={b.id} type="button"
                  onClick={() => setSelectedBrands((s) => active ? s.filter((x) => x !== b.id) : [...s, b.id])}
                  data-testid={`audit-brand-${b.id}`}
                  className={
                    'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ' +
                    (active
                      ? 'bg-gold/15 text-gold border-gold/30'
                      : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10')
                  }
                >
                  {b.name}
                </button>
              );
            })}
            {selectedBrands.length > 0 ? (
              <button type="button" onClick={() => setSelectedBrands([])}
                className="px-3 py-1.5 rounded-lg text-xs font-medium border bg-transparent text-white/50 border-white/10 hover:bg-white/5 inline-flex items-center gap-1">
                <X size={12} /> Clear
              </button>
            ) : null}
          </div>
        </div>

        {items.length === 0 ? (
          <EmptyState icon={<ScrollText size={20} />} title="No audit events match" subtitle="Adjust filters or clear them to see all platform events." />
        ) : (
          <Table testID="admin-audit-table">
            <THead>
              <Tr>
                <Th>Action</Th>
                <Th>Brand</Th>
                <Th>Target</Th>
                <Th>Actor</Th>
                <Th>When</Th>
              </Tr>
            </THead>
            <TBody>
              {items.map((row) => {
                const isOpen = expanded === row.id;
                const bid = row.metadata?.brand_id;
                return (
                  <>
                    <Tr key={row.id} onClick={() => setExpanded(isOpen ? null : row.id)}>
                      <Td><span className="font-mono text-xs text-gold">{row.action}</span></Td>
                      <Td>{bid && brandsById.get(bid)?.name ? <span className="text-xs">{brandsById.get(bid).name}</span> : <span className="text-white/30">—</span>}</Td>
                      <Td>
                        <div className="text-xs">{row.target_type}</div>
                        <div className="text-[11px] font-mono text-white/40">{(row.target_id || '').slice(0, 12)}</div>
                      </Td>
                      <Td><span className="text-xs font-mono text-white/70">{row.actor_email || '—'}</span></Td>
                      <Td><span className="text-xs text-white/60">{formatDateTime(row.created_at)}</span></Td>
                    </Tr>
                    {isOpen ? (
                      <Tr><Td className="bg-black/30" colSpan={5}><JsonViewer data={row.metadata || {}} /></Td></Tr>
                    ) : null}
                  </>
                );
              })}
            </TBody>
          </Table>
        )}

        <div className="text-[11px] text-white/35 mt-4">
          {items.length.toLocaleString('id-ID')} events shown
          {selectedBrands.length > 0 ? ` · ${selectedBrands.length} brand${selectedBrands.length > 1 ? 's' : ''} filtered` : ''}
          .
        </div>
      </SectionCard>
    </div>
  );
}
